import React, { useState, useRef, useEffect } from 'react';
import styles from './MultiSelectDropdown.module.css';

const MultiSelectDropdown = ({ 
	label, 
	options = [], 
	selectedValues = [], 
	onChange, 
	placeholder = 'Select options',
	displayFormat 
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	// Handle checkbox toggle
	const handleToggle = (value) => {
		if (selectedValues.includes(value)) {
			// Remove from selection
			onChange(selectedValues.filter(v => v !== value));
		} else {
			// Add to selection
			onChange([...selectedValues, value]);
		}
	};

	// Get display text for the button
	const getDisplayText = () => {
		if (displayFormat) {
			return displayFormat(selectedValues, options);
		}
		
		if (selectedValues.length === 0) {
			return placeholder;
		}
		
		if (selectedValues.length === 1) {
			// Find the name for single selection
			const selected = options.find(opt => opt[0] === selectedValues[0]);
			return selected ? selected[1] : '1 selected';
		}
		
		return `${selectedValues.length} selected`;
	};

	// Handle "Select All" / "Deselect All"
	const handleSelectAll = () => {
		if (selectedValues.length === options.length) {
			// Deselect all
			onChange([]);
		} else {
			// Select all
			onChange(options.map(opt => opt[0]));
		}
	};

	return (
		<div className={styles.multiselect_container} ref={dropdownRef}>
			{label && (
				<label className={styles.label}>
					{label}
				</label>
			)}
			
			<div className={styles.dropdown_wrapper}>
				<button
					type="button"
					className={styles.dropdown_button}
					onClick={() => setIsOpen(!isOpen)}
					aria-expanded={isOpen}
				>
					<span className={styles.button_text}>{getDisplayText()}</span>
					<span className={`${styles.arrow} ${isOpen ? styles.arrow_up : ''}`}>▼</span>
				</button>

				{isOpen && (
					<div className={styles.dropdown_panel}>
						{/* Select All / Deselect All option */}
						{options.length > 1 && (
							<>
								<div 
									className={`${styles.dropdown_item} ${styles.select_all}`}
									onClick={handleSelectAll}
								>
									<input
										type="checkbox"
										checked={selectedValues.length === options.length}
										onChange={handleSelectAll}
										onClick={(e) => e.stopPropagation()}
									/>
									<label>
										{selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
									</label>
								</div>
								<div className={styles.divider} />
							</>
						)}

						{/* Individual options */}
						{options.length > 0 ? (
							options.map((option) => {
								const [value, displayName] = option;
								const isChecked = selectedValues.includes(value);
								
								return (
									<div
										key={value}
										className={styles.dropdown_item}
										onClick={() => handleToggle(value)}
									>
										<input
											type="checkbox"
											checked={isChecked}
											onChange={() => handleToggle(value)}
											onClick={(e) => e.stopPropagation()}
										/>
										<label>{displayName}</label>
									</div>
								);
							})
						) : (
							<div className={styles.empty_message}>No options available</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default MultiSelectDropdown;

